import { useEffect, useRef, useState } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { downloadFile } from '@/lib/download';
import { Alert, Button } from '@/components/ui';
import { Can } from '@/components/Can';

export interface ExportItem {
  label: string;
  /** API path, from /api/v1 — the format is added from the item below it. */
  path: string;
  params: Record<string, string | number | undefined | null>;
}

/**
 * The download button on a report.
 *
 * Both formats of every document sit behind one button, because a shop needs
 * the PDF and its accountant needs the CSV, and neither should have to hunt
 * for the other. Each entry says what period it will cover, since an export
 * that quietly used a different range than the screen would be worse than no
 * export at all.
 */
export function ExportMenu({
  items,
  period,
  label = 'Download',
}: {
  items: ExportItem[];
  /** What the reader sees on screen, said plainly. */
  period?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  // Selected one field at a time: an object built inside the selector is a
  // new object on every render, which makes the store think it changed.
  const token = useAppSelector((state) => state.auth.accessToken);
  const organizationId = useAppSelector((state) => state.tenant.organizationId);
  const branchId = useAppSelector((state) => state.tenant.branchId);

  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent): void => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const run = async (item: ExportItem, format: 'pdf' | 'csv'): Promise<void> => {
    const key = `${item.label}-${format}`;
    setFailure(null);
    setBusy(key);
    try {
      const result = await downloadFile(
        item.path,
        { ...item.params, format },
        { token, organizationId, branchId },
      );
      if (!result.saved) setFailure(result.error);
      else setOpen(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Can permission={P.REPORT_EXPORT}>
      <div className="relative" ref={container}>
        <Button
          variant="outline"
          icon={Download}
          onClick={() => setOpen((was) => !was)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {label}
        </Button>

        {open && (
          <div
            role="menu"
            className="surface absolute right-0 z-20 mt-2 w-80 rounded-xl p-2 shadow-lg"
          >
            {period && (
              <p className="px-2 pb-2 pt-1 text-xs text-muted">
                Covering {period}. Every export states its period, who produced it and what was
                left out.
              </p>
            )}

            {items.map((item) => (
              <div key={item.label} className="mb-1 last:mb-0">
                <p className="px-2 py-1 text-xs font-medium text-secondary">{item.label}</p>
                <div className="flex gap-1">
                  <FormatButton
                    icon={FileText}
                    label="PDF"
                    hint="read and file"
                    busy={busy === `${item.label}-pdf`}
                    onClick={() => void run(item, 'pdf')}
                  />
                  <FormatButton
                    icon={FileSpreadsheet}
                    label="CSV"
                    hint="check the figures"
                    busy={busy === `${item.label}-csv`}
                    onClick={() => void run(item, 'csv')}
                  />
                </div>
              </div>
            ))}

            {failure && (
              <div className="mt-2">
                <Alert tone="danger">{failure}</Alert>
              </div>
            )}
          </div>
        )}
      </div>
    </Can>
  );
}

function FormatButton({
  icon: Icon,
  label,
  hint,
  busy,
  onClick,
}: {
  icon: typeof FileText;
  label: string;
  hint: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={busy}
      onClick={onClick}
      className="flex flex-1 items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-sunken disabled:opacity-60"
    >
      {busy ? (
        <Loader2 className="size-4 shrink-0 animate-spin" />
      ) : (
        <Icon className="size-4 shrink-0 text-secondary" />
      )}
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted">{hint}</span>
      </span>
    </button>
  );
}
